import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MatchStatus, SPORT } from "service";
import { ReactComponent as FootballIcon } from "../assets/football.svg";
import { ReactComponent as BasketballIcon } from "../assets/basketball.svg";
import { ReactComponent as VolleyballIcon } from "../assets/volleyball.svg";
import { ReactComponent as BaseballIcon } from "../assets/baseball.svg";
import { ReactComponent as IceHockeyIcon } from "../assets/icehockey.svg";
import { ReactComponent as TennisIcon } from "../assets/tennis.svg";
import { ReactComponent as PlayIcon } from "../assets/play.svg";
import { useMatchDictionary } from "../hooks/useMatchDictionary";
import {
  formatMinuteLabel,
  resolveScore,
  resolveTeamName,
} from "../utils/match-status";
import {
  FINISHED_MATCH_TTL_MS,
  FinishedMatchMeta,
  isMatchFinished,
  resolveMinuteDisplay,
} from "../utils/match-selector";
import { Text } from "../components/common/Text";

type SortOrder = "default" | "asc" | "desc";

type SportTab = {
  id: SPORT;
  label: string;
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
};

const ACTIVE_SPORT_STORAGE_KEY = "match-selector-active-sport";
const SPORT_VALUES: SPORT[] = Object.values(SPORT).filter(
  (value): value is SPORT => typeof value === "number"
);

const PAGE_SIZE = 10;

const resolveInitialSport = (): SPORT => {
  if (typeof window === "undefined") return SPORT.FOOTBALL;
  const stored = window.localStorage.getItem(ACTIVE_SPORT_STORAGE_KEY);
  if (!stored) return SPORT.FOOTBALL;
  const parsed = Number(stored);
  if (Number.isFinite(parsed) && SPORT_VALUES.includes(parsed as SPORT)) {
    return parsed as SPORT;
  }
  return SPORT.FOOTBALL;
};

const persistActiveSport = (sport: SPORT) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ACTIVE_SPORT_STORAGE_KEY, String(sport));
  } catch {
    // Ignore storage failures (private mode, etc.)
  }
};

const SPORT_TABS: SportTab[] = [
  { id: SPORT.FOOTBALL, label: "Football", Icon: FootballIcon },
  { id: SPORT.BASKETBALL, label: "Basketball", Icon: BasketballIcon },
  { id: SPORT.TENNIS, label: "Tennis", Icon: TennisIcon },
  { id: SPORT.VOLLEYBALL, label: "Volleyball", Icon: VolleyballIcon },
  { id: SPORT.BASEBALL, label: "Baseball", Icon: BaseballIcon },
  { id: SPORT.ICE_HOCKEY, label: "Ice Hockey", Icon: IceHockeyIcon },
];

type FinishedMatchMap = Record<string, FinishedMatchMeta>;

const hasTeamNameData = (status?: MatchStatus) => {
  if (!status) return false;
  const home = status?.T1?.n ?? status?.t1?.n ?? status?.T1n;
  const away = status?.T2?.n ?? status?.t2?.n ?? status?.T2n;
  const hasHome = typeof home === "string" && home.trim().length > 0;
  const hasAway = typeof away === "string" && away.trim().length > 0;
  return hasHome && hasAway;
};

export const MatchSelector: FC = () => {
  const [activeSport, setActiveSport] = useState<SPORT>(() =>
    resolveInitialSport()
  );
  const { dictionary, isLoading, isRefreshing } =
    useMatchDictionary(activeSport);
  const [finishedMatches, setFinishedMatches] = useState<FinishedMatchMap>({});
  const [animatedScores, setAnimatedScores] = useState<Record<string, boolean>>(
    {}
  );
  const [isTeamDataLoading, setIsTeamDataLoading] = useState<boolean>(true);
  const [sortOrder, setSortOrder] = useState<SortOrder>("default");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const scoreCacheRef = useRef<Record<string, string>>({});
  const animationTimeoutsRef = useRef<
    Record<string, ReturnType<typeof setTimeout>>
  >({});
  const prevActiveSportRef = useRef<SPORT>(activeSport);
  const prevBaseMatchesRef = useRef<MatchStatus[]>([]);

  const [visibleCountsBySport, setVisibleCountsBySport] = useState<
    Record<SPORT, number>
  >(() => {
    const initial: Partial<Record<SPORT, number>> = {};
    SPORT_VALUES.forEach((sport) => {
      initial[sport] = PAGE_SIZE;
    });
    return initial as Record<SPORT, number>;
  });

  const handleSportChange = useCallback((sport: SPORT) => {
    setActiveSport(sport);
    setIsTeamDataLoading(true);
    persistActiveSport(sport);
  }, []);

  const handleSortToggle = useCallback(() => {
    setSortOrder((prev) => {
      if (prev === "default") return "asc";
      if (prev === "asc") return "desc";
      return "default";
    });
  }, []);

  const handleTabRefresh = useCallback(() => {
    if (typeof window === "undefined") return;
    window.location.reload();
  }, []);

  const getMatchSortKey = useCallback(
    (match: MatchStatus): number => {
      const sport = match.s ?? activeSport;

      const seconds =
        typeof match?.tm === "number" && !Number.isNaN(match.tm)
          ? match.tm
          : null;
      const minute =
        typeof seconds === "number" ? Math.floor(seconds / 60) : null;
      const period =
        typeof match?.pc === "number" && !Number.isNaN(match.pc) ? match.pc : 0;

      // Basketball: use raw tm (seconds) as sort key
      if (sport === SPORT.BASKETBALL) {
        return typeof seconds === "number" ? seconds : 0;
      }

      // Tennis & Volleyball: sort by set number (pc)
      if (sport === SPORT.TENNIS || sport === SPORT.VOLLEYBALL) {
        return period;
      }

      // Default: minute-based, fallback to period
      if (typeof minute === "number") return minute;
      return period;
    },
    [activeSport]
  );

  const baseMatches = useMemo(() => {
    const list = dictionary[activeSport];
    if (Array.isArray(list)) return list;
    return [];
  }, [activeSport, dictionary]);

  useEffect(() => {
    const raw = searchTerm.trim();

    // If less than 3 characters, clear debounced search immediately
    if (raw.length < 3) {
      if (debouncedSearch !== "") {
        setDebouncedSearch("");
      }
      return;
    }

    // Otherwise debounce for 1 second
    const handle = setTimeout(() => {
      setDebouncedSearch(raw);
    }, 1000);

    return () => {
      clearTimeout(handle);
    };
  }, [searchTerm, debouncedSearch]);

  const visibleMatches = useMemo(() => {
    const now = Date.now();
    const dedupedIds = new Set(baseMatches.map((m) => m.id));
    const combined = [...baseMatches];
    Object.entries(finishedMatches).forEach(([matchKey, meta]) => {
      if (
        meta.sport === activeSport &&
        meta.expiresAt > now &&
        !dedupedIds.has(matchKey)
      ) {
        dedupedIds.add(matchKey);
        combined.push({ id: matchKey } as Partial<MatchStatus> as MatchStatus);
      }
    });

    // Filter by team name using debouncedSearch (min 3 chars)
    let filtered = combined;
    const query = debouncedSearch.trim().toLowerCase();
    if (query.length >= 3) {
      filtered = combined.filter((match) => {
        const homeName = resolveTeamName(match, "", "home").toLowerCase();
        const awayName = resolveTeamName(match, "", "away").toLowerCase();
        return homeName.includes(query) || awayName.includes(query);
      });
    }

    // Apply sorting
    if (sortOrder === "default") {
      return filtered;
    }

    const sorted = [...filtered].sort((a, b) => {
      const keyA = getMatchSortKey(a);
      const keyB = getMatchSortKey(b);
      return sortOrder === "asc" ? keyA - keyB : keyB - keyA;
    });

    return sorted;
  }, [
    activeSport,
    baseMatches,
    finishedMatches,
    sortOrder,
    getMatchSortKey,
    debouncedSearch,
  ]);

  const totalMatches = visibleMatches.length;
  const visibleCountForActive = visibleCountsBySport[activeSport] ?? PAGE_SIZE;
  const paginatedMatches = useMemo(
    () => visibleMatches.slice(0, visibleCountForActive),
    [visibleMatches, visibleCountForActive]
  );

  const hasMoreMatches = visibleCountForActive < totalMatches;

  const handleLoadMore = useCallback(() => {
    if (!totalMatches) return;
    setVisibleCountsBySport((prev) => {
      const current = prev[activeSport] ?? PAGE_SIZE;
      const next = Math.min(current + PAGE_SIZE, totalMatches);
      return { ...prev, [activeSport]: next };
    });
  }, [activeSport, totalMatches]);

  const isUpdating = isLoading || isRefreshing || isTeamDataLoading;
  const shouldShowUpdatingBadge = isUpdating && totalMatches === 0;
  const shouldRenderLoadingState = isLoading || isTeamDataLoading;
  const activeLabel =
    SPORT_TABS.find((sport) => sport.id === activeSport)?.label || "Matches";

  useEffect(() => {
    if (prevActiveSportRef.current !== activeSport) {
      prevActiveSportRef.current = activeSport;
      setIsTeamDataLoading(true);
    }
  }, [activeSport]);

  useEffect(() => {
    const prevMatches = prevBaseMatchesRef.current;
    const hasChanged =
      prevMatches.length !== baseMatches.length ||
      prevMatches.some((match, index) => match.id !== baseMatches[index].id);
    if (hasChanged) {
      setIsTeamDataLoading(true);
    }
    prevBaseMatchesRef.current = baseMatches;
  }, [baseMatches]);

  useEffect(() => {
    if (!visibleMatches.length) {
      setIsTeamDataLoading((prev) => (prev ? false : prev));
      return;
    }
    const allTeamNamesLoaded = visibleMatches.every((match) =>
      hasTeamNameData(match)
    );
    if (allTeamNamesLoaded) {
      setIsTeamDataLoading((prev) => (prev ? false : prev));
    }
  }, [visibleMatches]);

  useEffect(() => {
    if (!visibleMatches.length) return;
    setFinishedMatches((prev) => {
      const now = Date.now();
      let changed = false;
      const next: FinishedMatchMap = { ...prev };
      visibleMatches.forEach((match) => {
        const matchKey = match.id;
        if (!matchKey) return;
        const sport = match.s ?? activeSport;
        const prevEntry = prev[matchKey];
        if (isMatchFinished(match, sport)) {
          if (!prevEntry) {
            next[matchKey] = {
              sport,
              expiresAt: now + FINISHED_MATCH_TTL_MS,
            };
            changed = true;
          }
        } else if (prevEntry && prevEntry.sport === sport) {
          delete next[matchKey];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [activeSport, visibleMatches]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setFinishedMatches((prev) => {
        if (!Object.keys(prev).length) return prev;
        const now = Date.now();
        let changed = false;
        const next: FinishedMatchMap = {};
        Object.entries(prev).forEach(([matchKey, meta]) => {
          if (meta.expiresAt > now) {
            next[matchKey] = meta;
          } else {
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }, 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (!visibleMatches.length) {
      scoreCacheRef.current = {};
      setAnimatedScores({});
      Object.values(animationTimeoutsRef.current).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      animationTimeoutsRef.current = {};
      return;
    }

    const prevCache = scoreCacheRef.current;
    const nextCache: Record<string, string> = {};
    const updatedMatches: string[] = [];

    visibleMatches.forEach((match) => {
      const matchKey = match.id;
      if (!matchKey) return;
      const [homeScore, awayScore] = resolveScore(match, activeSport);
      const scoreValue = `${homeScore}-${awayScore}`;
      nextCache[matchKey] = scoreValue;

      if (
        prevCache[matchKey] !== undefined &&
        prevCache[matchKey] !== scoreValue
      ) {
        updatedMatches.push(matchKey);
      }
    });

    scoreCacheRef.current = nextCache;

    if (!updatedMatches.length) return;

    setAnimatedScores((prev) => {
      const next = { ...prev };
      updatedMatches.forEach((matchKey) => {
        next[matchKey] = true;
      });
      return next;
    });

    updatedMatches.forEach((matchKey) => {
      if (animationTimeoutsRef.current[matchKey]) {
        clearTimeout(animationTimeoutsRef.current[matchKey]);
      }
      animationTimeoutsRef.current[matchKey] = setTimeout(() => {
        setAnimatedScores((prev) => {
          if (!prev[matchKey]) return prev;
          const { [matchKey]: _, ...rest } = prev;
          return rest;
        });
        delete animationTimeoutsRef.current[matchKey];
      }, 1200);
    });
  }, [activeSport, visibleMatches]);

  useEffect(() => {
    return () => {
      Object.values(animationTimeoutsRef.current).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
    };
  }, []);

  return (
    <div className="flex flex-col w-full gap-3 lg:gap-4">
      <div className="flex flex-col lg:flex-row items-center lg:gap-3 lg:px-10 2xl:px-20">
        <div className="w-full -mx-3 lg:mx-0">
          <div className="flex flex-row gap-3 overflow-x-auto lg:overflow-x-visible px-3 lg:px-0 py-4 w-full">
            {SPORT_TABS.map(({ id, label, Icon }) => {
              const isActive = id === activeSport;
              const baseClasses =
                "inline-flex shrink-0 items-center justify-center rounded-lg px-4 lg:px-5 h-16 transition-[width,background,box-shadow,border] duration-300 ease-in-out";
              const stateClasses = isActive
                ? "border-2 border-primary shadow-[0_0_15px_theme(colors.primary)]"
                : "border border-border";
              const widthClasses = isActive ? "w-44" : "w-20";
              const labelClasses =
                "font-extrabold whitespace-nowrap overflow-hidden transition-[max-width,opacity,margin,transform] duration-300 ease-in-out";
              const labelState = isActive
                ? "max-w-[160px] opacity-100 ml-3 translate-x-0"
                : "max-w-0 opacity-0 ml-0 -translate-x-1";
              return (
                <button
                  key={label}
                  type="button"
                  aria-pressed={isActive}
                  aria-label={label}
                  onClick={() => handleSportChange(id)}
                  className={`${baseClasses} ${stateClasses} ${widthClasses}`}
                >
                  <Icon className="h-5 w-5 lg:h-[30px] lg:w-[30px]" />
                  <Text
                    as="span"
                    variant="landingTabLabel"
                    className={`${labelClasses} ${labelState}`}
                  >
                    {label}
                  </Text>
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex-shrink-0 flex w-full lg:w-1/4 px-3 lg:px-0">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search team"
            className="w-full h-12 lg:h-16 rounded-md border border-border bg-transparent px-3 py-2 text-sm lg:text-base text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          />
        </div>
      </div>

      <div className="overflow-hidden border border-border bg-gray-700 mx-3 md:mx-5 lg:mx-10 2xl:mx-20">
        <div className="flex items-center justify-between border-b border-border bg-gray-600 px-3 py-2 lg:px-5 lg:py-3">
          <h2 className="m-0 text-sm lg:text-lg font-bold text-white">
            {activeLabel}
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSortToggle}
              className="rounded-lg border border-primary bg-primary/10 px-2 py-1 text-xs lg:text-sm font-bold text-primary hover:bg-primary/20 transition-colors"
              title={`Sort: ${
                sortOrder === "default"
                  ? "Default"
                  : sortOrder === "asc"
                  ? "Ascending"
                  : "Descending"
              }`}
            >
              {sortOrder === "default" && (
                <span className="flex items-center gap-1">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                    />
                  </svg>
                  <span className="hidden lg:inline">Sort</span>
                </span>
              )}
              {sortOrder === "asc" && (
                <span className="flex items-center gap-1">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
                    />
                  </svg>
                  <span className="hidden lg:inline">Asc</span>
                </span>
              )}
              {sortOrder === "desc" && (
                <span className="flex items-center gap-1">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4"
                    />
                  </svg>
                  <span className="hidden lg:inline">Desc</span>
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={handleTabRefresh}
              className="rounded-lg border border-primary bg-primary/10 px-2 py-1 text-xs lg:text-sm font-bold text-primary hover:bg-primary/20 transition-colors"
              title="Refresh tab"
              aria-label="Refresh tab"
            >
              <span className="flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.5 9.75a7.5 7.5 0 0112.618-5.303L19.5 6.75M19.5 6.75V2.25m0 4.5h-4.5M19.5 14.25a7.5 7.5 0 01-12.618 5.303L4.5 17.25M4.5 17.25v4.5m0-4.5h4.5"
                  />
                </svg>
                <span className="hidden lg:inline">Refresh</span>
              </span>
            </button>
            <div className="rounded-lg border border-primary bg-primary/10 px-2 py-1 text-xs lg:text-sm font-bold text-primary">
              {shouldShowUpdatingBadge
                ? "Updating..."
                : `${totalMatches} match${totalMatches === 1 ? "" : "es"}`}
            </div>
          </div>
        </div>

        {shouldRenderLoadingState ? (
          <div className="flex min-h-96 h-full px-5 py-8 items-center justify-center text-center text-gray-300">
            Loading match list...
          </div>
        ) : totalMatches === 0 ? (
          <div className="flex min-h-96 h-full px-5 py-8 items-center justify-center text-center text-gray-300">
            No matches for this sport yet.
          </div>
        ) : (
          <div className="flex flex-col">
            {paginatedMatches.map((match) => {
              const matchKey = match.id;
              if (!matchKey) return null;
              const [homeScore, awayScore] = resolveScore(match, activeSport);
              const minuteLabel = formatMinuteLabel(match, activeSport);
              const minuteDisplay = resolveMinuteDisplay(
                match,
                activeSport,
                minuteLabel
              );
              const homeTeamName = resolveTeamName(match, "", "home");
              const awayTeamName = resolveTeamName(match, "", "away");
              return (
                <div
                  key={matchKey}
                  className="flex flex-row justify-between items-center gap-4 border-b border-border px-3 lg:px-5 py-3 last:border-0 hover:bg-gray-800"
                >
                  <span className="flex flex-col items-center justify-center text-center rounded bg-white min-w-10 max-w-10 lg:min-w-20 lg:max-w-20 px-2 py-1 text-[10px] font-semibold lg:text-xs lg:font-bold text-black leading-tight">
                    <span className="whitespace-nowrap">
                      {minuteDisplay.responsiveTopText ? (
                        <>
                          <span className="lg:hidden">
                            {minuteDisplay.responsiveTopText.mobile}
                          </span>
                          <span className="hidden lg:inline">
                            {minuteDisplay.responsiveTopText.desktop}
                          </span>
                        </>
                      ) : (
                        minuteDisplay.topText || "--"
                      )}
                    </span>
                    {minuteDisplay.bottomText && (
                      <span className="text-[10px] lg:text-xs font-extrabold whitespace-nowrap">
                        {minuteDisplay.bottomText}
                      </span>
                    )}
                  </span>
                  <div className="min-w-0 w-full">
                    <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center gap-2 lg:gap-6 text-sm lg:text-lg font-medium">
                      <span className="truncate text-right">
                        {homeTeamName}
                      </span>
                      <span
                        className={`flex min-w-16 max-w-16 lg:min-w-24 lg:max-w-24 items-center justify-center rounded bg-border py-2 text-xs lg:text-lg font-extrabold score-badge ${
                          animatedScores[matchKey] ? "score-update" : ""
                        }`}
                      >
                        {homeScore} - {awayScore}
                      </span>
                      <span className="truncate text-left">{awayTeamName}</span>
                    </div>
                  </div>
                  <a
                    href={`/${matchKey}`}
                    className="justify-self-end inline-flex items-center justify-center gap-1 rounded-md bg-primary w-fit min-h-8 h-fit px-1 lg:px-4 lg:py-2 text-sm font-bold text-white transition duration-200 hover:-translate-y-[1px] hover:bg-primary-hover"
                  >
                    <PlayIcon className="h-5 w-5" />
                    <span className="hidden lg:block">Live</span>
                  </a>
                </div>
              );
            })}

            {hasMoreMatches && (
              <button
                type="button"
                onClick={handleLoadMore}
                className="w-full py-3 text-sm lg:text-base font-semibold text-primary hover:text-primary-hover bg-gray-900 hover:bg-gray-900/80 transition"
              >
                Load more
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
